import riot from 'riot'
import logger from '../logger'
import {
  isPlainObject,
  merge,
  isFunction,
  get,
} from 'lodash-es'

export default function extendedObservable(subject, ModelNameSpace, modelDefault, getters) {
  const observers = []
  const temp = {}

  const extendGetters = (getters) => {
    for (const [key, value] of Object.entries(getters)) {
      if (typeof value.get === 'function') value.get = value.get.bind(subject)
      Object.defineProperty(subject.model.getters, key, value)
    }
  }

  if (typeof ModelNameSpace !== 'string') throw new Error('`ModelNameSpace` must be a valid string')
  if (ModelNameSpace.length <= 0) throw new Error('`ModelNameSpace` must be at least one character in length')

  // If the default for the model is [] then don't try to add a 'getters'
  // property because that will convert it to an object
  let defaultValue
  if (isPlainObject(modelDefault) || modelDefault === undefined) {
    defaultValue = merge({ getters: {} }, modelDefault) || { getters: {} }
  } else {
    defaultValue = modelDefault
  }

  // extend the subject adding the extended observable methods
  Object.defineProperties(subject, {
    model: {
      value: defaultValue,
      enumerable: true,
      writable: true,
      configurable: false,
    },

    registerObserver: {
      value(observer) {
        logger('registering observer', 'debug')()

        // Don't double register
        if (observers.indexOf(observer) > -1) return

        observers.push(observer)
        temp[ModelNameSpace] = this.model
        if (isFunction(observer.update)) {
          observer.update(temp)
        } else {
          observer.trigger('update', temp)
          observer[ModelNameSpace] = this.model // eslint-disable-line no-param-reassign
        }
      },
      enumerable: true,
      writable: false,
      configurable: false,
    },

    unregisterObserver: {
      value(observer) {
        logger('unregistering observer', 'debug')()
        observers.splice(observers.indexOf(observer), 1)
      },
      enumerable: true,
      writable: false,
      configurable: false,
    },

    notifyObservers: {
      value: (function notifyObvs(fireMe) {
        return new Promise(
          (resolve) => {
            logger(`${ModelNameSpace} notifying ${observers.length} observers`, 'debug')()
            observers.forEach((observer) => {
              temp[ModelNameSpace] = this.model
              if (get(fireMe, 'event')) {
                observer.trigger(fireMe.event, temp)
              } else if (isFunction(observer.update)) {
                observer.update(temp)
              } else {
                observer.trigger('update', temp)
              }
            }, this)
            resolve()
          }
        )
      }.bind(subject)),
      enumerable: true,
      writable: false,
      configurable: false,
    },
  })

  Object.defineProperties(subject.model, {
    safeGet: {
      value(key, defaultVal) {
        logger(`Getting ${key} from ${ModelNameSpace}`, 'debug')()
        return get(this, key, defaultVal)
      },
      enumerable: false,
      writable: false,
      configurable: false,
    },
  })

  riot.observable(subject)
  if (typeof getters === 'object') extendGetters(getters)

  return subject
}
